/**
 * Flow Queuing System — the enroll-queue provider's UI-signal layer,
 * extracted verbatim from `EnrollQueueContext.tsx` (F9 god-context cut 4 of
 * 4): docked-sidebar/review open state, the sidebar tab + its unseen-sends
 * blip, the contact-jump and queue-flash signals, the Requests-tab
 * selection/search seed, and the thin call wrappers the context value
 * exposes (`selectInputOption`, `refreshInputOptionsIfStale`, `removeItem`,
 * `unstickItem`, `sendItems`, `sendSkipLabelFor`).
 *
 * The mutation objects those wrappers CALL are NOT extracted — they stay
 * declared in `EnrollQueueContext.tsx`, reserved for the F1/DBOS worktree,
 * and are handed to this hook by reference (see `QueueUiSignalsDeps`). This
 * hook never creates, reorders, or reaches into one.
 *
 * Pure move: every body, dependency array and doc comment below is
 * byte-identical to its previous home. The only additions are the hook
 * wrapper, its parameter object, and its return object. The setters the
 * still-in-context `sendMutation` needs (`setSendSkipReasons`,
 * `setIsOpen`, `setActiveTab`) are returned for exactly
 * that reason.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '../../../lib/api';
import { WriteRefusedError, fetchJson } from '../../../lib/apiFetchJson';
import { toStripView } from '../../../lib/stripView';
import { QueueItemExecutionResponseSchema, SendResponseSchema, type InputFieldOptions, type ItemPatch, type SendResponse, type SendSkip, type StagedSendConfig } from '@shared/flow/enrollment';
import type { RequestEntry } from '@shared/flow/requests';
import { contactsInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import { openPanel } from '../shell-next/panels/flow-panels-store';
import { prependOptimisticRequest } from './requests-hooks';
import { SEND_SKIP_REASON_LABELS } from './itemBlockReason';
import { INPUT_OPTIONS_KEY, QUEUE_KEY } from './queue-query-keys';
import type { ItemOpQueue } from './useItemOpQueue';
import {
  PatchQueueItemError,
  type ContactJumpHandler,
  type ContactJumpRequest,
  type EnrollQueueContextValue,
  type QueuePlayLanding,
  type PendingSendItemSnapshot,
  type QueueFlashRequest,
  type SendItemsOutcome,
  type SidebarTab,
} from './enroll-queue-types';

/**
 * The `salesforce_warm_intro` mirror's real refresh cadence (Fix 5, MAJOR bd
 * finding) — matches `SFDC_OBJECT_SYNC_CRON` (`SfdcObjectSyncWorkflow.ts`,
 * backend-owned, hourly). Kept in sync BY HAND, not by import: this is a
 * frontend bundle and that constant lives in server-only code (same
 * disclaimer as `SUGGESTED_INVITE_LABEL` in StatePills.tsx). Used both as
 * the input-options query's `staleTime` divisor (Fix 5b) and as the
 * threshold `refreshInputOptionsIfStale` (Fix 5a) checks a cached entry's
 * age against.
 */
const WARM_INTRO_SYNC_CADENCE_MS = 60 * 60 * 1000;

/**
 * What this hook borrows from the context file. The three mutations are
 * typed structurally (just the `mutateAsync` each wrapper calls) rather than
 * as `UseMutationResult`s: the F1/DBOS worktree owns those objects and can
 * change everything else about them without touching this file. They are
 * passed WHOLE (not pre-destructured) so each wrapper keeps the exact
 * dependency array it had in its previous home.
 */
export interface QueueUiSignalsDeps {
  patchItem: EnrollQueueContextValue['patchItem'];
  removeMutation: { mutateAsync: (itemId: number) => Promise<unknown> };
  unstickMutation: { mutateAsync: (itemId: number) => Promise<unknown> };
  /**
   * D23 (flow-contacts-bulk-send QA round 3, item 11) — the SAME per-item
   * destructive-operation serial queue `resetItemToBase`/`assignPlay` already
   * run through (`EnrollQueueContext.tsx`, built by D12). `removeItem` below
   * wraps its whole body in this so `opPendingItems` (folded into
   * `EnrollQueueContext.isItemBusy`) goes true the instant a remove click
   * lands — before the DELETE's response resolves — which is what disables
   * every mounted remove control for free, with no new prop on `AssignedRow`.
   * It also orders a remove against a concurrent reset/reassignment for the
   * same item, which a bespoke ref guard would not.
   */
  runItemOp: ItemOpQueue['runItemOp'];
  /**
   * D23 (Codex adversarial follow-up, round 3 batch) — `removeItem`'s 404
   * catch path needs to invalidate contact-state the same way the success
   * path does (`EnrollQueueContext.tsx`'s `removeMutation.onSuccess`), not
   * just `QUEUE_KEY`. A benign double-fire 404 still means the item is gone
   * server-side, and if it was ALREADY gone before this client's cache knew
   * (a cross-session delete, the other reachable 404 case per this file's
   * doc comment below) the contact's "Queued" pill can otherwise go stale
   * indefinitely — nothing else in this codebase invalidates that key on
   * this path.
   */
  invalidateContactState: () => void;
  /**
   * D12 Codex round-2 fix #6 — typed as resolving with the validated
   * `SendResponse` (which is what the context's `mutationFn` already returns)
   * rather than `unknown`: `sendItems` below has to surface the server's
   * SKIPPED ids to its caller so the send pipelines can roll back the
   * materialization of rows that never actually went out. Still structural,
   * not a `UseMutationResult` — the F1/DBOS worktree keeps ownership of
   * everything else about the mutation object.
   *
   * terra-ohe.7 (F1 U6) — `mutateAsync` now takes the optional `staged` wire
   * snapshot alongside `itemIds` (`SendSchema.staged`), matching the
   * context's `sendMutation.mutationFn` shape.
   */
  sendMutation: { mutateAsync: (input: { itemIds: number[]; staged?: StagedSendConfig }) => Promise<SendResponse> };
}

export type QueueUiSignals = Pick<
  EnrollQueueContextValue,
  | 'isOpen'
  | 'open'
  | 'close'
  | 'toggle'
  | 'activeTab'
  | 'setActiveTab'
  | 'rightOwner'
  | 'openQueueTab'
  | 'openChat'
  | 'openCustomInvite'
  | 'openNotes'
  | 'queueFlashRequest'
  | 'consumeQueueFlashRequest'
  | 'requestQueueFlash'
  | 'queuePlayLanding'
  | 'requestPlayCardLanding'
  | 'dismissQueuePlayLanding'
  | 'selectedRequestId'
  | 'openRequestDetail'
  | 'closeRequestDetail'
  | 'requestsSearchSeed'
  | 'openRequestsForContact'
  | 'contactJumpRequest'
  | 'jumpToContact'
  | 'registerContactJumpHandler'
  | 'selectInputOption'
  | 'refreshInputOptionsIfStale'
  | 'removeItem'
  | 'unstickItem'
  | 'sendItems'
  | 'sendSkipLabelFor'
> & {
  /** Wholesale-replaced by the still-in-context `sendMutation.onSuccess`. */
  setSendSkipReasons: (reasons: Map<number, SendSkip['reason']>) => void;
  /**
   * D9 regression #2 fix (Codex adversarial follow-up on the row-level Send
   * rework) — clears ONE item's stale server-authoritative skip label
   * without a new send response. `sendSkipReasons` used to be replaceable
   * only wholesale (`setSendSkipReasons`, above — every OTHER send
   * response), so a rep who fixed the underlying block via "Change Play",
   * "Send anyway", or any connector/sender fix (all funnel through
   * `patchItem`/`patchMutation`) kept seeing the STALE "will skip —
   * <reason>" label and a disabled row Send with no send round trip to
   * clear it. `EnrollQueueContext.patchMutation`'s `onSuccess` calls this
   * for every successful patch — see that call site's doc comment for why
   * "any successful patch" is the right generalized seam. No-ops when
   * nothing is recorded for the item.
   */
  clearSendSkipLabel: (itemId: number) => void;
  /** `finalizeSend`'s "View in Actions" toast action opens the dock. */
  setIsOpen: (open: boolean) => void;
};

export function useQueueUiSignals({ patchItem, removeMutation, unstickMutation, sendMutation, runItemOp, invalidateContactState }: QueueUiSignalsDeps): QueueUiSignals {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  /**
   * U4 behavior spec 5 — the LAST `POST /api/flow/queue/send` response's
   * `skipped` reasons, keyed by item id. Server admission is authoritative
   * over the client's own `itemBlockReason` guess: an item the client
   * thought was sendable can still come back skipped (a race with a
   * concurrent send, a mirror sweep that ran between page-load and click).
   * Replaced WHOLESALE on every send response (never merged with the prior
   * one) so a stale reason from an earlier, unrelated batch never lingers
   * once a newer response has landed.
   */
  const [sendSkipReasons, setSendSkipReasons] = useState<Map<number, SendSkip['reason']>>(new Map());
  // 'queue' is the default landing surface — Todos (R5's default landing
  // surface) was removed 2026-08-18.
  const [activeTab, setActiveTab] = useState<SidebarTab>('queue');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [requestsSearchSeed, setRequestsSearchSeed] = useState<{ value: string; contactId?: string } | null>(null);
  const [contactJumpRequest, setContactJumpRequest] = useState<ContactJumpRequest | null>(null);
  const jumpNonceRef = useRef(0);
  // Queue-row flash target consumed by QueueBody (see QueueFlashRequest's doc
  // comment on EnrollQueueContextValue for why this is kept wired even though
  // its original producer, the Todos→Plays deep link, was removed).
  const [queueFlashRequest, setQueueFlashRequest] = useState<QueueFlashRequest | null>(null);
  // Same capture-then-compare-across-an-async-gap shape as `jumpNonceRef`
  // below — a dedicated counter (not `jumpNonceRef` itself) since the two
  // requests are independent primitives with independent consumers.
  const queueFlashNonceRef = useRef(0);
  // By Play card landing target consumed by `PlayQueueCard` (U4) — see
  // `queuePlayLanding`'s doc comment on EnrollQueueContextValue. Shares
  // `queueFlashNonceRef` above on purpose: the direct-enroll path requests
  // both a row flash and a card landing, and one counter keeps the two
  // nonce spaces disjoint.
  const [queuePlayLanding, setQueuePlayLanding] = useState<QueuePlayLanding | null>(null);
  // U2 (plan 2026-09-25-003, Codex r3 #3) — a REGISTRATION SET, not a single
  // mutable slot. The Plays tab mounts THREE contact tables at once (plan
  // 2026-09-25-002), each registering its own handler; a single slot meant
  // whichever table's mount effect ran LAST owned the only registration, so
  // collapsing (unmounting) that one table's accordion cleared the slot even
  // though the OTHER two tables were still mounted and one of them might
  // have owned the jumped-to contact's row — "View on contact →" then
  // navigated away from a page that still had the row. See
  // `registerContactJumpHandler`'s doc comment on `EnrollQueueContextValue`
  // for the full contract. Not a StrictMode lifetime ref (no mount-scoped
  // "cancelled" flag to reset): `Set.add` of the same function twice is a
  // no-op and a stale `delete` of an already-removed handler is a no-op, so
  // register/unregister stays idempotent under the dev double-invoke with no
  // extra bookkeeping.
  const contactJumpHandlersRef = useRef<Set<ContactJumpHandler>>(new Set());
  const registerContactJumpHandler = useCallback((handler: ContactJumpHandler) => {
    contactJumpHandlersRef.current.add(handler);
    return () => {
      contactJumpHandlersRef.current.delete(handler);
    };
  }, []);
  // R4/Q4/U2: the always-mounted fallback for a "View on contact →" jump —
  // this Provider lives for the app's lifetime (mounted once at shell
  // level), unlike any one contact table. Tries every registered handler in
  // REGISTRATION ORDER (insertion order = mount order; the Plays tab's three
  // sections are disjoint by definition — responded / not responded / not in
  // play — so at most one handler can ever find a given contact) and stops
  // at the first one that reports `true`:
  //  - some handler handled it -> done, nothing further to do.
  //  - NO handlers registered at all (no contact table mounted anywhere) ->
  //    NAVIGATE to the Flow contacts view focused on this contact (Q4)
  //    rather than dead-ending with a toast — the exact bug that closed.
  //  - one or more handlers WERE registered but none found the row -> at
  //    least one table is genuinely mounted and already scanned its loaded
  //    rows; there's nothing else to navigate to, so this is the toast's
  //    real job.
  useEffect(() => {
    if (!contactJumpRequest) return;
    const handlers = contactJumpHandlersRef.current;
    let handled = false;
    for (const handler of handlers) {
      if (handler(contactJumpRequest.contactId)) {
        handled = true;
        break;
      }
    }
    if (handled) return;
    if (handlers.size === 0) {
      navigate(contactsInCommandCenter(contactJumpRequest.contactId));
      return;
    }
    toast.error("Contact isn't in the current view");
  }, [contactJumpRequest, navigate]);

  /**
   * U8 / Task 4 (round-2 QA feedback) — the server-resolved options fields'
   * write. TWO kinds exist now: the connector picker (`persistsTo:
   * 'warm_intro_id'`) and the Preferred Channel picklist (`persistsTo:
   * 'preferred_channel'`). Switches on `persistsTo` — the field's OWN
   * discriminator (`InputFieldOptionsSchema`) — never on `fieldKey`, which is
   * owned by the play type's definition file server-side and would be drift
   * waiting to happen if mirrored here. The reference itself (e.g. does this
   * warm intro belong to THIS item's contact?) is validated server-side in
   * `patchQueueItem`.
   */
  const selectInputOption = useCallback(
    async (itemId: number, _fieldKey: string, persistsTo: InputFieldOptions['persistsTo'], value: string | null) => {
      try {
        // Explicit switch, not a computed `{ [persistsTo]: value }` key —
        // `preferred_channel`'s column is a strict two-literal enum on
        // `ItemPatch`, which a generic `string | null` can't satisfy without
        // this narrowing.
        if (persistsTo === 'preferred_channel') {
          await patchItem(itemId, { preferred_channel: value as ItemPatch['preferred_channel'] });
        } else {
          await patchItem(itemId, { warm_intro_id: value });
        }
      } catch (err) {
        // Fix 5a (MAJOR bd finding): a 400 here means the picked connector
        // row vanished server-side (patchQueueItem's InvalidPatchError for
        // an unknown warm_intro_id) — the hourly `salesforce_warm_intro`
        // sync deleted it between page-load and click. Invalidate THIS
        // item's cached options so the picker refetches and heals (shows
        // the current live list) instead of silently keeping a dead choice
        // highlighted until the next unrelated refetch.
        if (err instanceof PatchQueueItemError && err.status === 400) {
          void queryClient.invalidateQueries({ queryKey: [...INPUT_OPTIONS_KEY, itemId] });
        }
        throw err;
      }
    },
    [patchItem, queryClient],
  );

  const refreshInputOptionsIfStale = useCallback(
    (itemId: number) => {
      const state = queryClient.getQueryState([...INPUT_OPTIONS_KEY, itemId]);
      // Never fetched (no observer yet, or the item isn't in `groups` at
      // all) — nothing cached to be stale. `dataUpdatedAt` is 0 until the
      // first successful fetch lands.
      if (!state || state.dataUpdatedAt === 0) return;
      if (Date.now() - state.dataUpdatedAt > WARM_INTRO_SYNC_CADENCE_MS) {
        void queryClient.invalidateQueries({ queryKey: [...INPUT_OPTIONS_KEY, itemId] });
      }
    },
    [queryClient],
  );

  /**
   * D23 (flow-contacts-bulk-send QA round 3, item 11) — rapid-click remove
   * used to have FOUR independent defects that composed into a duplicate
   * DELETE + a silently-swallowed 404: no re-entrancy guard of any kind, no
   * optimistic update, a fire-and-forget invalidation (`removeMutation`'s
   * `onSuccess` in `EnrollQueueContext.tsx` now awaits it, matching
   * `addMutation`/`bulkAddMutation`), and a floated rejection with no toast.
   * This closes the guard + toast half; the awaited invalidation closes the
   * window where a successful DELETE's row stayed mounted (and re-clickable)
   * for the whole refetch round trip.
   *
   * `runItemOp` claims the item SYNCHRONOUSLY before the first await, so a
   * second click landing in the same render as the first sees the row
   * already busy and its remove control already disabled — see
   * `QueueUiSignalsDeps.runItemOp`'s doc comment. try/catch + status
   * discrimination mirrors `unstickItem`'s exact shape below: a 404 means
   * the row is already gone server-side — most often THIS call's own benign
   * double-fire (an earlier successful DELETE's invalidation already
   * repainted the row away), but also reachable if something else removed
   * the item between page-load and click. Either way there is nothing to
   * tell the rep — invalidate so the cache is guaranteed to agree, and stay
   * silent. Any OTHER failure (network error, 500, unexpected status) is
   * genuinely unexpected and gets a real error toast.
   */
  const removeItem = useCallback(
    async (itemId: number) => {
      await runItemOp(itemId, async () => {
        try {
          await removeMutation.mutateAsync(itemId);
        } catch (err) {
          if (err instanceof PatchQueueItemError && err.status === 404) {
            // Codex adversarial follow-up (round 3 batch) — both AWAITED
            // (matching the success path's `onSuccess`, not the fire-and-forget
            // this used to be) so the busy lock stays held until the cache is
            // guaranteed to agree, and BOTH keys invalidated (not just
            // `QUEUE_KEY`) since the 404 is also reachable when something else
            // (a cross-session delete) removed the item before this client's
            // contact-state cache ever heard about it — leaving a "Queued" pill
            // stale indefinitely otherwise.
            invalidateContactState();
            await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
            return;
          }
          // apiFetch already announced a refusal.
          if (err instanceof WriteRefusedError) return;
          toast.error("Couldn't remove from queue — try again");
        }
      });
    },
    [runItemOp, removeMutation, queryClient, invalidateContactState],
  );

  /**
   * POST .../unstick — see `unstickMutation` above for the onSettled
   * rationale. Never rejects: a 400 (`ItemNotStuckError` — the item already
   * moved on, a self-resolving race) is swallowed silently, since
   * `onSettled`'s `QUEUE_KEY` invalidation already repaints the row's true
   * state and there is nothing else to tell the rep. Any OTHER failure
   * (network error, 500, an unexpected status) surfaces a toast — this is
   * NOT expected/self-resolving, so silently swallowing it would leave the
   * rep with a dead button and no explanation.
   */
  const unstickItem = useCallback(
    async (itemId: number) => {
      try {
        await unstickMutation.mutateAsync(itemId);
      } catch (err) {
        if (err instanceof PatchQueueItemError && err.status === 400) return;
        // apiFetch already announced a refusal.
        if (err instanceof WriteRefusedError) return;
        toast.error("Couldn't reset the item — try again");
      }
    },
    [unstickMutation],
  );

  /**
   * D12 Codex round-2 fix #6 — resolves with the ADMISSION RESULT, not
   * `void`. Both send pipelines used to treat "the promise resolved" as "the
   * items were sent", which is only true for `accepted` ids: a SKIPPED item
   * comes back inside a perfectly successful 202, so it never reached either
   * pipeline's rejection path and its send-time materialization stayed
   * committed on a row that was never sent — a silent, incorrect override.
   *
   * The response is re-parsed through `SendResponseSchema` rather than read
   * off the (already-validated) result directly. `validateResponse`
   * logs-and-returns a shape-mismatched payload AS-IS by design — it never
   * throws — so `response.skipped` is not guaranteed to be an array at
   * runtime, and this function must not start rejecting on a degraded
   * response that previously resolved fine. A payload that fails the parse
   * reports NO skipped ids: nothing is rolled back, which is exactly the
   * pre-fix behavior for a response we cannot trust.
   */
  const sendItems = useCallback(
    async (itemIds: number[], staged?: StagedSendConfig): Promise<SendItemsOutcome> => {
      const response = await sendMutation.mutateAsync({ itemIds, staged });
      const parsed = toStripView(SendResponseSchema).safeParse(response);
      if (!parsed.success) return { acceptedIds: [], skippedIds: [] };
      return {
        acceptedIds: parsed.data.accepted,
        skippedIds: parsed.data.skipped.map(skip => skip.item_id),
      };
    },
    [sendMutation],
  );

  /**
   * U4 behavior spec 5 — the LAST send response's server-authoritative skip
   * label for one item, or `null` (nothing skipped for it, or no send has
   * been attempted yet). Maps the raw `SendSkip.reason` enum through
   * `SEND_SKIP_REASON_LABELS` — the ONE enum-to-copy mapping, shared with
   * whatever the client-side `itemBlockReason` check would have said, so a
   * caller never needs to know which of the two produced the label.
   */
  const sendSkipLabelFor = useCallback(
    (itemId: number): string | null => {
      const reason = sendSkipReasons.get(itemId);
      return reason ? (SEND_SKIP_REASON_LABELS[reason] ?? 'Skipped by server') : null;
    },
    [sendSkipReasons],
  );

  // See the `clearSendSkipLabel` field's doc comment on `QueueUiSignals`.
  // Uses the functional-update form directly on the real `useState` setter
  // (safe HERE, unlike at the `patchMutation.onSuccess` call site: that
  // seam only receives `setSendSkipReasons` typed as a plain value setter,
  // never the updater-function overload) so a concurrent send response
  // landing in the same tick can never be clobbered by a stale snapshot.
  const clearSendSkipLabel = useCallback((itemId: number) => {
    setSendSkipReasons(prev => {
      if (!prev.has(itemId)) return prev;
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  // rightOwner: ALWAYS derived, never its own useState — see the doc comment
  // on EnrollQueueContextValue.
  const rightOwner = isOpen ? 'sidebar' : 'none';
  // U9: the sole dock FAB's toggle — a plain open<->close that preserves
  // `activeTab` verbatim. It replaces a contract that restored a remembered
  // `lastSidebarTab`.
  // `close()` is reused rather than inlining `setIsOpen(false)` (coding
  // standards' "Compose With" rule).
  const toggle = useCallback(() => {
    if (isOpen) close();
    else setIsOpen(true);
  }, [isOpen, close]);
  // R2: opens the sidebar on the Queue tab — see openQueueTab's doc comment
  // on EnrollQueueContextValue for why this exists as its own call rather
  // than callers composing open() + setActiveTab('queue') themselves.
  const openQueueTab = useCallback(() => { setIsOpen(true); setActiveTab('queue'); }, []);
  // See openChat's doc comment on EnrollQueueContextValue.
  const openChat = useCallback(() => openPanel('chat'), []);
  // See openCustomInvite's doc comment on EnrollQueueContextValue.
  const openCustomInvite = useCallback(() => { setIsOpen(true); setActiveTab('customInvite'); }, []);
  // See openNotes's doc comment on EnrollQueueContextValue.
  const openNotes = useCallback(() => { setIsOpen(true); setActiveTab('notes'); }, []);
  // See EnrollQueueContextValue.consumeQueueFlashRequest — functional update +
  // nonce guard so a stale timer can't clear a newer pending flash.
  const consumeQueueFlashRequest = useCallback((nonce: number) => {
    setQueueFlashRequest(current => (current && current.nonce === nonce ? null : current));
  }, []);
  // See EnrollQueueContextValue.requestQueueFlash's doc comment.
  const requestQueueFlash = useCallback((contactId: string, opts?: { autoOpenPicker?: boolean; openEditorAfterAssign?: boolean }) => {
    queueFlashNonceRef.current += 1;
    setQueueFlashRequest({
      contactId,
      nonce: queueFlashNonceRef.current,
      autoOpenPicker: opts?.autoOpenPicker,
      openEditorAfterAssign: opts?.openEditorAfterAssign,
    });
  }, []);
  // See EnrollQueueContextValue.requestPlayCardLanding's doc comment. Shares
  // the flash nonce counter (see `queuePlayLanding`'s state declaration).
  const requestPlayCardLanding = useCallback((target: { playTypeId: number; playInstanceRef: string; added: number; skipped: number; source: 'single' | 'bulk' }) => {
    queueFlashNonceRef.current += 1;
    setQueuePlayLanding({ ...target, nonce: queueFlashNonceRef.current });
  }, []);
  // Same functional-update + nonce guard as `consumeQueueFlashRequest`.
  const dismissQueuePlayLanding = useCallback((nonce: number) => {
    setQueuePlayLanding(current => (current && current.nonce === nonce ? null : current));
  }, []);
  // openRequestDetail is the ONE call U7 (post-fire) and U8 (pill deep-link)
  // both need: opens the sidebar, switches to Requests, and drills straight
  // into the detail view — no separate "switch tab then select" two-step for
  // callers outside this tab.
  const openRequestDetail = useCallback((requestId: string) => {
    setIsOpen(true);
    setActiveTab('requests');
    setSelectedRequestId(requestId);
  }, [setActiveTab]);
  const closeRequestDetail = useCallback(() => setSelectedRequestId(null), []);
  /**
   * Auto-open-on-send (mark ready -> "Send 1" -> the sidebar drills straight
   * into the created request): called from `sendMutation`'s poll-settle
   * branch once EXACTLY one accepted item has been confirmed `submitted`.
   * `GET /api/flow/queue/send` is admission-only (item ids, no execution
   * id — SendResponseSchema's doc comment), and the real `execution_id`
   * lands on the row off the request path once `FlowSendWorkflow`'s
   * `finalizeItem` step writes it on `terra-worker` — this is the read
   * that recovers it (mirrors `ContactActionMenus.tsx`'s instant-action
   * fire-and-open flow).
   *
   * Confirmed-success feedback (`openRequestDetail`) is unconditional on a
   * resolved `execution_id` and never depends on the optimistic prepend
   * below succeeding (same R5 ordering ContactActionMenus uses) — a
   * cache-write failure can only ever cost the optimistic row, never the
   * jump itself. If the fetch fails entirely (network blip, 404 on a race),
   * this silently no-ops: the rep still sees the "Sent" toast, and the
   * Requests tab will show the real row on its own poll/SSE-triggered
   * refetch if they open it manually.
   *
   * This bead (U5, "never navigate") removed the ONLY call site of this
   * function (`sendMutation`'s settle branch — see its `finalizeSend` doc
   * comment) — "View in Actions" is a plain tab switch now, no per-execution
   * drill-in. Per the bead's explicit scope note, this stays DEFINED, not
   * deleted, in case a future single-item "View details" affordance wants
   * the same optimistic-prepend + execution-id resolution. The
   * oxlint-disable is intentional, not a placeholder for a forgotten wire-up.
   */
  // oxlint-disable-next-line no-unused-vars -- intentionally retained with no current caller; see doc comment above
  const openSentRequestDetail = useCallback(
    async (itemId: number, item: PendingSendItemSnapshot | null) => {
      try {
        // A non-2xx or malformed body throws into the best-effort catch below.
        const parsed = await fetchJson(
          apiFetch,
          QueueItemExecutionResponseSchema,
          `/api/flow/queue/items/${itemId}/execution`,
        );
        if (!parsed.execution_id) return;
        openRequestDetail(parsed.execution_id);
        try {
          const entry: RequestEntry = {
            id: parsed.execution_id,
            kind: 'action',
            contact: item ? { id: item.contactId, name: null, email: item.contactEmail } : null,
            actionName: item?.playName ?? 'Play sent',
            // The endpoint resolves the REAL Requests-feed status (mapped
            // from the linked ActionExecution's deliveryStatus) rather than
            // a hardcoded 'running' — a synchronous sender (event_invite) is
            // already `delivered` at send time, and showing "still
            // executing" for a run that's actually done would be wrong even
            // if only briefly (see QueueItemExecutionResponseSchema's doc
            // comment).
            status: parsed.status,
            ts: new Date().toISOString(),
            inputs: null,
            response: null,
            appliedSnapshot: null,
          };
          prependOptimisticRequest(queryClient, entry);
        } catch {
          // Best-effort only — same rationale as ContactActionMenus' prepend.
        }
      } catch {
        // Best-effort — see the doc comment above.
      }
    },
    [apiFetch, openRequestDetail, queryClient],
  );
  // A NEW object every call (not just the string) — see requestsSearchSeed's
  // doc comment on EnrollQueueContextValue for why: RequestsTab's consuming
  // effect must re-fire even when the SAME contact is seeded twice in a row
  // (e.g. the rep edited the search, then clicked "View all" again).
  const openRequestsForContact = useCallback((contactId: string, contactName: string) => {
    setIsOpen(true);
    setActiveTab('requests');
    setRequestsSearchSeed({ value: contactName, contactId });
  }, [setActiveTab]);
  const jumpToContact = useCallback((contactId: string) => {
    jumpNonceRef.current += 1;
    setContactJumpRequest({ contactId, nonce: jumpNonceRef.current });
    // Matches the locked prototype's jumpToContact(): return to the list
    // (NOT close the whole sidebar — it's a non-modal dock, the contact
    // table stays visible beside it) so the rep sees both at once.
    setSelectedRequestId(null);
  }, []);

  return {
    isOpen,
    open,
    close,
    toggle,
    activeTab,
    setActiveTab,
    rightOwner,
    openQueueTab,
    openChat,
    openCustomInvite,
    openNotes,
    queueFlashRequest,
    consumeQueueFlashRequest,
    requestQueueFlash,
    queuePlayLanding,
    requestPlayCardLanding,
    dismissQueuePlayLanding,
    selectedRequestId,
    openRequestDetail,
    closeRequestDetail,
    requestsSearchSeed,
    openRequestsForContact,
    contactJumpRequest,
    jumpToContact,
    registerContactJumpHandler,
    selectInputOption,
    refreshInputOptionsIfStale,
    removeItem,
    unstickItem,
    sendItems,
    sendSkipLabelFor,
    setSendSkipReasons,
    clearSendSkipLabel,
    setIsOpen,
  };
}
